import { useEffect, useState } from "react";
import { Store } from "@tanstack/react-store";
import { useStore } from "@tanstack/react-store";
import { onAuthStateChanged, signOut, type User } from "firebase/auth";
import { auth } from "@/firebase/app";
import { queryClient } from "@/trpc/client";
import {
  readTestAuthSession,
  testAuthUser,
  TEST_AUTH_STORAGE_KEY,
} from "@/lib/testAuth";

export interface AuthState {
  user: User | null;
  isLoading: boolean;
}

export const authStore = new Store<AuthState>({
  user: null,
  isLoading: true,
});

let authReadyPromise: Promise<void> | null = null;


function clearQueryCacheOnAuthChange(uid: string | null, previousUid: string | null | undefined) {
  if (previousUid !== undefined && previousUid !== uid) {
    // Cancel signals are dispatched synchronously, so any in-flight fetches
    // abort and can't repopulate the cache after we clear it. We deliberately
    // do NOT await cancelQueries — clearing must happen before
    // authStore.setState propagates, otherwise consumers reading the cache in
    // the same tick (e.g. the login mutation's onSuccess calling
    // getEntryWorkspace) can observe the previous user's data.
    void queryClient.cancelQueries();
    queryClient.clear();
  }
}

export function initAuth(): Promise<void> {
  if (!authReadyPromise) {
    // #441: a dev-only injected session short-circuits Firebase entirely — no
    // onAuthStateChanged subscription, no emulator round-trip — so headless Chromium can
    // render auth-gated routes with :9099 unavailable. `readTestAuthSession` returns null
    // unless this is a `vite dev` build with the key set, and the whole branch is
    // statically dead in a production build (see lib/testAuth.ts).
    // `import.meta.env.DEV` is replaced with a literal at build time, so in a production
    // build this reads `false ? … : null` — the call, and with it the whole testAuth
    // module, is eliminated. Calling readTestAuthSession() unguarded would keep the module
    // in the bundle even though it can never return a session (verified by
    // `scripts/assert-test-auth-stripped.mjs`, which failed exactly that way first).
    const testSession = import.meta.env.DEV ? readTestAuthSession() : null;
    if (testSession) {
      // Published on a macrotask rather than synchronously, so the injected session
      // arrives the way the real one does: after the first render, via a callback. The
      // prerendered HTML is always the signed-out tree, and matching that ordering keeps
      // hydration identical between the two paths.
      authReadyPromise = new Promise<void>((resolve) => {
        setTimeout(() => {
          // Re-read the session inside the timeout rather than closing over the
          // `testSession` captured above: logout() (or an external clear of the
          // storage key) can run before this macrotask fires. Publishing the
          // captured value then would re-sign-in the just-logged-out user. If the
          // key is gone, resolve without touching the store.
          const current = readTestAuthSession();
          if (current) {
            authStore.setState((prev) => ({
              ...prev,
              user: testAuthUser(current) as unknown as User,
              isLoading: false,
            }));
          }
          resolve();
        }, 0);
      });
      return authReadyPromise;
    }
    authReadyPromise = new Promise<void>((resolve) => {
      let previousUid: string | null | undefined = undefined;

      onAuthStateChanged(auth, (user) => {
        const uid = user?.uid ?? null;
        clearQueryCacheOnAuthChange(uid, previousUid);
        previousUid = uid;

        authStore.setState((prev) => ({ ...prev, user, isLoading: false }));
        resolve();
      });
    });
  }
  return authReadyPromise;
}

export function logout() {
  // #441: with an injected dev session there is no Firebase session to sign out of —
  // clear the key and publish the signed-out state, or the smoke script's signOut step
  // would appear to succeed while the app stayed signed in.
  if (import.meta.env.DEV && readTestAuthSession()) {
    try {
      globalThis.localStorage?.removeItem(TEST_AUTH_STORAGE_KEY);
    } catch {
      /* storage can throw; the state update below is what matters */
    }
    // Mirror the Firebase path's uid-change cache clear: without it, data
    // fetched while signed in via the injected session stays cached and
    // visible after logout within the same tab. Clear before the setState
    // propagates, same ordering as clearQueryCacheOnAuthChange.
    void queryClient.cancelQueries();
    queryClient.clear();
    authStore.setState((prev) => ({ ...prev, user: null, isLoading: false }));
    // The injected-session branch of initAuth returned early WITHOUT subscribing
    // to onAuthStateChanged, so Firebase auth changes go unobserved. Now that the
    // session key is cleared (readTestAuthSession() is null), re-arm initAuth so it
    // takes the real path — otherwise a subsequent real Firebase sign-in in the
    // same tab would leave authStore.user null and the /dashboard guard would keep
    // redirecting, until a full page reload.
    authReadyPromise = null;
    void initAuth();
    return Promise.resolve();
  }
  return signOut(auth);
}

/**
 * Hydration-safe "is the user logged in?" check for prerendered routes.
 * Prerender builds with `user: null`, so reading `authStore` directly during
 * the first client render would diverge from the static HTML. We mirror the
 * prerender output until after mount, then surface the real auth state.
 */
export function useIsLoggedInAfterMount(): boolean {
  const user = useStore(authStore, (s) => s.user);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted && !!user;
}

/**
 * Like {@link useIsLoggedInAfterMount}, but also reports whether auth has
 * actually resolved yet (`resolved === mounted && !isLoading`). Callers that
 * branch on `isLoggedIn` — e.g. redirect vs. show-a-link — can hold off
 * rendering until `resolved` is true, so a signed-in user never flashes the
 * signed-out view before the state settles.
 *
 * Gating on `!isLoading` (not merely `mounted`) matters: Firebase resolves
 * `onAuthStateChanged` asynchronously, so for several frames after mount the
 * store still holds `user: null, isLoading: true`. Reporting `resolved` at
 * mount would surface that transient signed-out state as final and render the
 * wrong branch. `isLoggedIn` is meaningless while `resolved` is false and
 * mirrors the prerender output (`false`).
 */
export function useAuthAfterMount(): { resolved: boolean; isLoggedIn: boolean } {
  const user = useStore(authStore, (s) => s.user);
  const isLoading = useStore(authStore, (s) => s.isLoading);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const resolved = mounted && !isLoading;
  return { resolved, isLoggedIn: resolved && !!user };
}
